// frontend/src/utils/sessionValidation.js
// Purpose: Advanced session validation with expiry warnings and admin permission checks

import { jwtDecode } from 'jwt-decode';

/**
 * Configuration for session validation
 */
const SESSION_CONFIG = {
  WARNING_BEFORE_EXPIRY: 5 * 60 * 1000, // 5 minutes in milliseconds
  CHECK_INTERVAL: 60 * 1000, // Check every minute
  WARNING_SHOWN_KEY: 'sessionWarningShown',
};

/**
 * Decodes and validates JWT token
 * @param {string} token - JWT token to validate
 * @returns {Object|null} Decoded token payload or null if invalid
 */
export const decodeJWT = (token) => {
  try {
    if (!token) return null;
    return jwtDecode(token);
  } catch (error) {
    console.warn('[sessionValidation] Failed to decode JWT:', error.message);
    return null;
  }
};

/**
 * Checks if JWT token is expired
 * @param {string} token - JWT token to check
 * @returns {boolean} True if token is expired
 */
export const isTokenExpired = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;
  
  const now = Math.floor(Date.now() / 1000);
  return decoded.exp < now;
};

/**
 * Gets time until token expires in milliseconds
 * @param {string} token - JWT token to check
 * @returns {number} Milliseconds until expiry (negative if expired)
 */
export const getTimeUntilExpiry = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return -1;
  
  const now = Math.floor(Date.now() / 1000);
  return (decoded.exp - now) * 1000;
};

/**
 * Checks if session is valid and not expired
 * @returns {boolean} True if session is valid
 */
export const isSessionValid = () => {
  const token = localStorage.getItem('jwtToken');
  if (!token) return false;
  
  return !isTokenExpired(token);
};

/**
 * Checks if user has admin permissions based on JWT
 * @returns {boolean} True if user is admin
 */
export const hasAdminPermissions = () => {
  const token = localStorage.getItem('jwtToken');
  if (!token) return false;
  
  const decoded = decodeJWT(token);
  if (!decoded) return false;
  
  // Check for admin role or high role level
  return decoded.role === 'admin' || 
         decoded.role === 'super_admin' || 
         (decoded.role_level && decoded.role_level >= 3);
};

/**
 * Checks if session is about to expire and shows warning
 * @returns {boolean} True if warning was shown
 */
export const checkAndShowExpiryWarning = () => {
  const token = localStorage.getItem('jwtToken');
  if (!token) return false;
  
  const timeUntilExpiry = getTimeUntilExpiry(token);
  const warningShown = localStorage.getItem(SESSION_CONFIG.WARNING_SHOWN_KEY);
  
  // Show warning if within warning period and not already shown
  if (timeUntilExpiry > 0 && 
      timeUntilExpiry <= SESSION_CONFIG.WARNING_BEFORE_EXPIRY && 
      !warningShown) {
    
    localStorage.setItem(SESSION_CONFIG.WARNING_SHOWN_KEY, 'true');
    
    // Dispatch custom event for UI to show warning
    window.dispatchEvent(new CustomEvent('sessionExpiryWarning', {
      detail: {
        timeUntilExpiry,
        message: `Your session will expire in ${Math.ceil(timeUntilExpiry / 60000)} minutes. Please save your work.`
      }
    }));
    
    return true;
  }
  
  return false;
};

/**
 * Validates session before making admin API calls
 * @param {boolean} requireAdmin - Whether admin permissions are required
 * @returns {Object} Validation result with isValid, shouldRedirect, and message
 */
export const validateSessionForAdminCall = (requireAdmin = true) => {
  const token = localStorage.getItem('jwtToken');
  
  // No token - definitely invalid
  if (!token) {
    return {
      isValid: false,
      shouldRedirect: true,
      message: 'No authentication token found. Please log in.',
      reason: 'NO_TOKEN'
    };
  }
  
  // Check if token is expired
  if (isTokenExpired(token)) {
    return {
      isValid: false,
      shouldRedirect: true,
      message: 'Your session has expired. Please log in again.',
      reason: 'TOKEN_EXPIRED'
    };
  }
  
  // Check admin permissions if required
  if (requireAdmin && !hasAdminPermissions()) {
    return {
      isValid: false,
      shouldRedirect: true,
      message: 'You do not have permission to access this feature.',
      reason: 'INSUFFICIENT_PERMISSIONS'
    };
  }
  
  // Check if warning should be shown
  const warningShown = checkAndShowExpiryWarning();
  
  return {
    isValid: true,
    shouldRedirect: false,
    message: warningShown ? 'Session valid but expiring soon.' : 'Session valid.',
    reason: 'VALID',
    warningShown
  };
};

/**
 * Wrapper function for admin API calls with session validation
 * @param {Function} apiCall - The API function to call
 * @param {boolean} requireAdmin - Whether admin permissions are required
 * @returns {Promise} The API call result or throws error if validation fails
 */
export const withSessionValidation = async (apiCall, requireAdmin = true) => {
  const validation = validateSessionForAdminCall(requireAdmin);
  
  if (!validation.isValid) {
    const error = new Error(validation.message);
    error.validationResult = validation;
    throw error;
  }
  
  return apiCall();
};

/**
 * Starts session monitoring (checks expiry and shows warnings)
 * @returns {Function} Function to stop monitoring
 */
export const startSessionMonitoring = () => {
  const interval = setInterval(() => {
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      clearInterval(interval);
      return;
    }
    
    checkAndShowExpiryWarning();
    
    // Auto-logout if expired
    if (isTokenExpired(token)) {
      clearInterval(interval);
      window.dispatchEvent(new CustomEvent('sessionExpired'));
    }
  }, SESSION_CONFIG.CHECK_INTERVAL);
  
  // Return function to stop monitoring
  return () => clearInterval(interval);
};

/**
 * Clears session warning flag (useful after user acknowledges warning)
 */
export const clearSessionWarning = () => {
  localStorage.removeItem(SESSION_CONFIG.WARNING_SHOWN_KEY);
};

/**
 * Gets session information for debugging
 * @returns {Object} Session details
 */
export const getSessionInfo = () => {
  const token = localStorage.getItem('jwtToken');
  if (!token) {
    return { valid: false, message: 'No token found' };
  }
  
  const decoded = decodeJWT(token);
  if (!decoded) {
    return { valid: false, message: 'Invalid token' };
  }
  
  const timeUntilExpiry = getTimeUntilExpiry(token);
  const isExpired = timeUntilExpiry < 0;
  
  return {
    valid: !isExpired,
    expired: isExpired,
    timeUntilExpiry,
    timeUntilExpiryFormatted: isExpired ? 
      'Expired' : 
      `${Math.ceil(timeUntilExpiry / 60000)} minutes`,
    userRole: decoded.role,
    userLevel: decoded.role_level,
    isAdmin: hasAdminPermissions(),
    email: decoded.email,
    issuedAt: new Date(decoded.iat * 1000).toISOString(),
    expiresAt: new Date(decoded.exp * 1000).toISOString()
  };
};
